import React from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import { Box, Title, Paper, Text, Stack, Group, Badge, Anchor, Alert, ThemeIcon, Table, Breadcrumbs } from '@mantine/core';
import { TreeStructure, Lightbulb, Warning, Check } from '@phosphor-icons/react';
import SEO from '../../components/SEO';

export default function CampaignStructureGuide() {
  const guidesBase = useGuidesBase();
  return (
    <Box px="lg" py="md">
      <SEO
        title="Struktura kampanii Google Ads"
        description="Typy kampanii, grupy reklam i słowa kluczowe. Jak prawidłowo zbudować strukturę konta Google Ads dla najlepszych wyników."
        path={`${guidesBase}/campaign-structure`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Struktura kampanii</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Struktura kampanii Google Ads</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">Dobrze zorganizowana struktura konta to fundament skutecznych kampanii. Wpływa na Quality Score, koszty i łatwość optymalizacji.</Text>
      </Alert>

      {/* Hierarchy */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Hierarchia konta Google Ads</Text>
        <Stack gap="xs">
          {[
            { level: 0, label: 'Konto', desc: 'Dane rozliczeniowe, dostęp użytkowników' },
            { level: 1, label: 'Kampania', desc: 'Budżet, lokalizacja, harmonogram, strategia stawek' },
            { level: 2, label: 'Grupa reklam', desc: 'Słowa kluczowe + reklamy o wspólnym temacie' },
            { level: 3, label: 'Reklamy + Słowa kluczowe', desc: 'Treści reklam dopasowane do słów kluczowych' },
          ].map((item, i) => (
            <Group key={i} gap="sm" wrap="nowrap" ml={item.level * 24}>
              <Badge size="xs" variant="filled" color={['gray', 'blue', 'brand', 'green'][item.level]}>{item.label}</Badge>
              <Text size="xs" c="dimmed">{item.desc}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>

      {/* Campaign types */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Typy kampanii</Text>
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Typ</Table.Th>
              <Table.Th>Kiedy używać</Table.Th>
              <Table.Th>Budżet min.</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {[
              ['Search', 'Użytkownicy aktywnie szukają produktu/usługi', '30-50 zł/dz.'],
              ['Performance Max', 'Szeroki zasięg, automatyzacja, e-commerce', '50-100 zł/dz.'],
              ['Display', 'Budowanie świadomości marki, remarketing', '20-50 zł/dz.'],
              ['Shopping', 'Sklepy internetowe z feedem produktowym', '30-80 zł/dz.'],
              ['Video (YouTube)', 'Branding, storytelling, zasięg', '50-100 zł/dz.'],
            ].map(([type, when, budget], i) => (
              <Table.Tr key={i}>
                <Table.Td><Text size="sm" fw={500}>{type}</Text></Table.Td>
                <Table.Td><Text size="sm">{when}</Text></Table.Td>
                <Table.Td><Text size="sm" ff="monospace">{budget}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Ad group strategies */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Strategie grup reklam</Text>
        <Stack gap="md">
          <Box>
            <Group gap="xs" mb={4}>
              <Badge size="sm" color="green" variant="light">Zalecane</Badge>
              <Text size="sm" fw={500}>STAG (Single Theme Ad Group)</Text>
            </Group>
            <Text size="sm" c="dimmed" lh={1.6}>
              Jedna grupa = jeden temat (3-10 słów kluczowych blisko powiązanych). Np. grupa "buty do biegania" zawiera: buty do biegania, obuwie biegowe, buty running. Łatwiejsza optymalizacja niż SKAG, wystarczająco precyzyjna.
            </Text>
          </Box>
          <Box>
            <Group gap="xs" mb={4}>
              <Badge size="sm" color="blue" variant="light">Zaawansowane</Badge>
              <Text size="sm" fw={500}>SKAG (Single Keyword Ad Group)</Text>
            </Group>
            <Text size="sm" c="dimmed" lh={1.6}>
              Jedna grupa = jedno słowo kluczowe w 2-3 dopasowaniach. Maksymalna kontrola, ale trudne w zarządzaniu przy dużych kontach. Google odchodzi od tego podejścia na rzecz automatyzacji.
            </Text>
          </Box>
          <Box>
            <Group gap="xs" mb={4}>
              <Badge size="sm" color="red" variant="light">Unikaj</Badge>
              <Text size="sm" fw={500}>Jedna grupa na wszystko</Text>
            </Group>
            <Text size="sm" c="dimmed" lh={1.6}>
              Wrzucanie wszystkich słów kluczowych do jednej grupy. Reklamy nie są dopasowane do zapytań, niski Quality Score, wysokie CPC.
            </Text>
          </Box>
        </Stack>
      </Paper>

      {/* Match types */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Typy dopasowań słów kluczowych</Text>
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Typ</Table.Th>
              <Table.Th>Składnia</Table.Th>
              <Table.Th>Przykład zapytania</Table.Th>
              <Table.Th>Zasięg</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {[
              ['Przybliżone (Broad)', 'buty sportowe', 'obuwie do ćwiczeń, sneakersy', 'Szeroki'],
              ['Fraza (Phrase)', '"buty sportowe"', 'buty sportowe damskie, tanie buty sportowe', 'Średni'],
              ['Dokładne (Exact)', '[buty sportowe]', 'buty sportowe, obuwie sportowe', 'Wąski'],
            ].map(([type, syntax, example, reach], i) => (
              <Table.Tr key={i}>
                <Table.Td><Text size="sm" fw={500}>{type}</Text></Table.Td>
                <Table.Td><Text size="sm" ff="monospace">{syntax}</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{example}</Text></Table.Td>
                <Table.Td><Badge size="xs" variant="light" color={['red', 'yellow', 'green'][i]}>{reach}</Badge></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Alert color="yellow" mt="sm" icon={<Warning size={14} />}>
          <Text size="xs">Unikaj samych dopasowań przybliżonych (Broad) bez wykluczeń. Generują dużo nieistotnego ruchu. Zacznij od Phrase + Exact, potem rozszerzaj.</Text>
        </Alert>
      </Paper>

      {/* Best practices */}
      <Paper withBorder p="lg" radius="md">
        <Text size="sm" fw={600} mb="sm">Zasady dobrej struktury</Text>
        <Stack gap="xs">
          {[
            'Oddziel kampanie brandowe od generycznych (różne budżety i stawki)',
            'Każda grupa reklam powinna mieć minimum 3 reklamy RSA',
            'Nie mieszaj Search z Display w jednej kampanii',
            'Ustaw budżet na poziomie kampanii, stawki na poziomie grupy',
            'Nazwy kampanii: "Typ - Kategoria - Zasięg" (np. "Search - Buty - Warszawa")',
            'Regularnie sprawdzaj raport wyszukiwanych haseł i dodawaj wykluczenia',
          ].map((tip, i) => (
            <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color="green" size="xs" radius="xl"><Check size={10} weight="bold" /></ThemeIcon>
              <Text size="sm" lh={1.5}>{tip}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
